import React from 'react';
import { useNavigate } from 'react-router-dom';
import PanchTatvaButton from '../components/PanchTatvaButton';

const Section = ({ title, children }) => (
  <div className="mb-8 md:mb-10">
    <h2 className="text-xl md:text-2xl font-heading text-headingText mb-3 md:mb-4">{title}</h2>
    <div className="text-sm md:text-base text-bodyText leading-relaxed space-y-3">{children}</div>
  </div>
);

const Table = ({ rows }) => (
  <div className="overflow-x-auto rounded-xl border border-gray-200 my-4">
    <table className="w-full text-sm md:text-base">
      <thead className="bg-secondary/10">
        <tr>
          {rows[0].map((h, i) => (
            <th key={i} className="px-4 py-3 text-left font-heading text-headingText">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.slice(1).map((row, ri) => (
          <tr key={ri} className={ri % 2 === 0 ? 'bg-primaryBg' : 'bg-cardBg'}>
            {row.map((cell, ci) => (
              <td key={ci} className="px-4 py-3 text-bodyText">{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function ReturnPolicy() {
  const navigate = useNavigate();
  return (
    <div className="w-full max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-20">
      {/* Header */}
      <div className="text-center mb-10 md:mb-14">
        <p className="text-secondary font-medium tracking-widest text-xs md:text-sm uppercase mb-3">Legal</p>
        <h1 className="text-3xl md:text-5xl font-heading text-headingText mb-4">Return & Refund Policy</h1>
        <p className="text-sm text-bodyText opacity-70">Last updated: March 22, 2026</p>
      </div>

      <div className="bg-cardBg rounded-2xl p-6 md:p-10 shadow-sm">

        <Section title="Our Philosophy on Returns">
          <p>
            At Panch Tatva, every product is handcrafted with care and intention. We want you to love what
            you receive. If something isn't right, we're here to make it so.
          </p>
        </Section>

        <Section title="Eligibility for Returns">
          <Table rows={[
            ['Condition', 'Eligible?'],
            ['Damaged or defective product', '✅ Yes — full refund or replacement'],
            ['Wrong item received', '✅ Yes — replacement at no cost'],
            ['Product opened and used', '❌ No — hygiene & safety reasons'],
            ['Change of mind (unopened)', '✅ Yes — within 7 days of delivery'],
            ['Gift sets (partial return)', '❌ No — must return complete set'],
          ]} />
        </Section>

        <Section title="Return Window">
          <p>
            You must initiate a return within <strong>7 days of delivery</strong>. Returns requested after
            7 days will not be accepted unless the product is defective.
          </p>
        </Section>

        <Section title="How to Initiate a Return">
          <ol className="list-decimal list-inside space-y-2 pl-2">
            <li>Email <a href="mailto:support@panch-tatva.co.in" className="text-secondary hover:underline">support@panch-tatva.co.in</a> with your order number</li>
            <li>Include photographs of the product (if damaged or wrong item)</li>
            <li>Our team will respond within 2 business days</li>
            <li>If approved, we'll arrange a reverse pickup or provide a return address</li>
            <li>Refund will be processed within 5–7 business days after we receive the product</li>
          </ol>
        </Section>

        <Section title="Refund Method">
          <p>
            Refunds are credited to the <strong>original payment method</strong> used at checkout (UPI,
            card, net banking, etc.). We do not offer cash refunds.
          </p>
          <p>
            If the original payment method is unavailable, we will issue store credit valid for 6 months.
          </p>
        </Section>

        <Section title="Non-Returnable Items">
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Products that have been opened or used</li>
            <li>Items purchased during final sale / clearance events</li>
            <li>Digital products or gift cards</li>
          </ul>
        </Section>

        <Section title="Cancellations">
          <p>
            Orders can be cancelled within <strong>12 hours of placement</strong>, before they are dispatched.
            To cancel, email us immediately with your order ID. Once dispatched, cancellations are not possible.
          </p>
        </Section>

      </div>

      <div className="mt-10 text-center">
        <PanchTatvaButton onClick={() => navigate(-1)} variant="outline">
          ← Go Back
        </PanchTatvaButton>
      </div>
    </div>
  );
}
